import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  // PayloadAction,
  // createAction,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { Company } from "admin/types/company";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { Meta } from "admin/types/meta";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";

const key = "company";
const adapter = createEntityAdapter<Company.Company, string>({
  selectId: (company) => company.code,
});

const initialStateValues: Company.CompanyState = {
  company: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchCompanies = createAsyncThunk<
  {
    companies: Company.Company[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchCompanies`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies.json?${param}`
        : "";
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      companies: result.data.companies,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

const fetchCompanyData = async ({
  context,
  isServerSide = false,
  headers,
  locale,
}: {
  context: PageContext.PageContext;
  isServerSide?: boolean;
  headers?: any;
  locale?: string;
}) => {
  const url =
    context.adminType === AdminTypes.manager
      ? `${API_BASE_PATH}/manager/companies/${context.companyCode}.json`
      : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company.json`;
  const result = await createWrapper({ isServerSide, locale }).get(url, {
    headers,
  });
  return {
    company: result.data.company,
  };
};

export const fetchCompany = createAsyncThunk<
  {
    company: Company.Company;
  },
  {
    context: PageContext.PageContext;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchCompany`, async (args, { rejectWithValue }) => {
  try {
    return fetchCompanyData(args);
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchCompanyIfNeeded = createAsyncThunk<
  any,
  {
    context: PageContext.PageContext;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
    state: RootState;
  }
>(
  `${key}/fetchCompanyIfNeeded`,
  async (args, { rejectWithValue }) => {
    try {
      return fetchCompanyData(args);
    } catch (err) {
      return rejectWithValue(err.data.errors);
    }
  },
  {
    condition: (args, { getState }) => {
      const { context } = args;
      const { company } = getState();
      const selectedCompany = company.company;
      // データが存在する場合はアクションをスキップ
      if (context.adminType === AdminTypes.manager) {
        return !(
          selectedCompany && selectedCompany?.code === context?.companyCode
        );
      } else {
        // adminは自社のみ取得できるはずなのでデータが存在すればスキップ
        return !selectedCompany;
      }
    },
  },
);

export const postCompany = createAsyncThunk<
  {
    company: Company.Company;
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postCompany`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies.json`
        : "";
    const result = await createWrapper().post(url, {
      company: params,
    });
    return {
      company: result.data.company,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putCompany = createAsyncThunk<
  {
    company: Company.Company;
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putCompany`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/company.json`;
    const result = await createWrapper().put(url, { company: params });
    return {
      company: result.data.company,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postUserAccountSetting = createAsyncThunk<
  {
    company: Company.Company;
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postUserAccountSetting`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/user_account_settings.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/user_account_settings.json`;
    const result = await createWrapper().post(url, {
      company: params,
    });
    return {
      company: result.data.company,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postUserImportSetting = createAsyncThunk<
  {
    company: Company.Company;
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postUserImportSetting`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/user_import_settings.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/user_import_settings.json`;
    const result = await createWrapper().post(url, {
      company: params,
    });
    return {
      company: result.data.company,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchCompanySystemUsageFees = createAsyncThunk<
  {
    companySystemUsageFees: Company.SystemUsageFee[];
  },
  {
    context: PageContext.PageContext;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchCompanySystemUsageFees`, async (args, { rejectWithValue }) => {
  try {
    const { context, isServerSide = false, headers, locale } = args;
    const url = `${API_BASE_PATH}/manager/companies/${context.companyCode}/system_usage_fees.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      companySystemUsageFees: result.data.companySystemUsageFees,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postCompanySystemUsageFees = createAsyncThunk<
  {
    companySystemUsageFees: Company.SystemUsageFee[];
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postCompanySystemUsageFees`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url = `${API_BASE_PATH}/manager/companies/${context.companyCode}/bulk_manage_system_usage_fees.json`;
    const result = await createWrapper().post(url, {
      company_system_usage_fees: params.company_system_usage_fees,
    });
    return {
      companySystemUsageFees: result.data.companySystemUsageFees,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postCompanyCreditCardFees = createAsyncThunk<
  {
    companyCreditCardFees: Company.CreditCardFee[];
  },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postCompanyCreditCardFees`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url = `${API_BASE_PATH}/manager/companies/${context.companyCode}/bulk_manage_credit_card_fees.json`;
    const result = await createWrapper().post(url, {
      company_credit_card_fees: params.company_credit_card_fees,
    });
    return {
      companyCreditCardFees: result.data.companyCreditCardFees,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const companySlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setCompany: (state, action) => {
      state.company = action.payload;
    },
    setCompanies: (state, action) => {
      adapter.setAll(state, action.payload.companies);
      state.meta = action.payload.meta;
    },
    clearCompany: (state) => {
      state.company = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.company };
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.companies);
        state.meta = action.payload.meta;
      })
      .addCase(fetchCompany.pending, (state, action) => {
        const context = action.meta.arg.context;
        // managerのみ複数のcompanyを扱うためチェックする
        if (context.adminType === AdminTypes.manager) {
          const targetCompanyCode = context?.companyCode;
          if (state.company && state.company.code !== targetCompanyCode) {
            // 別の会社データを取得するため選択中の会社をクリアする
            state.company = null;
          }
        }
      })
      .addCase(fetchCompany.fulfilled, (state, action) => {
        state.company = action.payload.company;
      })
      .addCase(fetchCompanyIfNeeded.pending, (state) => {
        // 常に別の会社データを取得するため選択中の会社をクリアする
        state.company = null;
      })
      .addCase(fetchCompanyIfNeeded.fulfilled, (state, action) => {
        state.company = action.payload.company;
      })
      .addCase(postCompany.fulfilled, (state, action) => {
        state.company = action.payload.company;
      })
      .addCase(putCompany.fulfilled, (state, action) => {
        state.company = action.payload.company;
      })
      .addCase(postUserAccountSetting.fulfilled, (state, action) => {
        state.company = action.payload.company;
      })
      .addCase(postUserImportSetting.fulfilled, (state, action) => {
        state.company = action.payload.company;
      })
      .addCase(fetchCompanySystemUsageFees.fulfilled, (state, action) => {
        state.company.companySystemUsageFees =
          action.payload.companySystemUsageFees;
      })
      .addCase(postCompanySystemUsageFees.fulfilled, (state, action) => {
        state.company.companySystemUsageFees =
          action.payload.companySystemUsageFees;
      })
      .addCase(postCompanyCreditCardFees.fulfilled, (state, action) => {
        state.company.companyCreditCardFees =
          action.payload.companyCreditCardFees;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: companyByIdSelector,
  selectIds: companyIdsSelector,
  selectEntities: companyEntitiesSelector,
  selectAll: companiesSelector,
  selectTotal: totalCompaniesSelector,
} = adapter.getSelectors((state: RootState) => state.company);

const stateSelector = (state: RootState) => state[key];

export const companySelector = createSelector(
  stateSelector,
  (state) => state.company,
);

export const companySystemUsageFeesSelector = createSelector(
  stateSelector,
  (state) => state.company.companySystemUsageFees,
);

export const companyCreditCardFeesSelector = createSelector(
  stateSelector,
  (state) => state.company.companyCreditCardFees,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const companiesMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

/** action export **/
export const { setCompany, setCompanies, clearCompany } = companySlice.actions;

export const convertCompanyFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.status === "" || params.status === null) {
    delete params.status;
  }
  return params;
};

export const convertCompanyToFormValues = (data: any) => {
  const values = { ...data };
  values.is_tax_payer = String(values.is_tax_payer);
  return values;
};
